import React, { useState, useEffect } from 'react';
import { 
    ShoppingBag, CheckCircle2, Clock, Truck, Package, 
    ArrowRight, ArrowLeft, RefreshCw, Filter, Search, DollarSign,
    AlertCircle, ExternalLink
} from 'lucide-react';
import { getOrdersByUser, getUserId } from '../api/client';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';

export default function SellerOrders() {
    const rtl = isRTL();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('ALL'); // ALL, PENDING, PROCESSING, SHIPPED, DELIVERED
    const [searchTerm, setSearchTerm] = useState('');

    const loadOrders = async () => {
        try {
            setLoading(true);
            const userId = getUserId() || localStorage.getItem('userId');
            if (userId) {
                const list = await getOrdersByUser(userId);
                if (Array.isArray(list)) {
                    setOrders(list);
                }
            }
        } catch (err) {
            console.error('Failed to load seller orders:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOrders();
    }, []);

    const filteredOrders = orders.filter(o => {
        if (activeFilter === 'PENDING' && o.status !== 'PENDING') return false;
        if (activeFilter === 'SHIPPED' && o.status !== 'SHIPPED') return false;
        if (activeFilter === 'DELIVERED' && o.status !== 'DELIVERED') return false;
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            return (o.orderNumber || '').toLowerCase().includes(term) ||
                   (o.trackingNumber || '').toLowerCase().includes(term);
        }
        return true;
    });

    const totalRevenue = orders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
    const pendingOrdersCount = orders.filter(o => o.status === 'PENDING' || o.status === 'PROCESSING').length;

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ margin: '0 0 6px 0', fontSize: 22, fontWeight: 800, color: 'var(--seller-text, #111827)' }}>
                        {rtl ? 'إدارة الطلبات والمبيعات' : 'Orders & Fulfillment Management'}
                    </h1>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--seller-muted, #64748b)' }}>
                        {rtl 
                            ? 'متابعة شحنات العملاء، تأكيد تجهيز المنتجات، وأرقام بوليصات الشحن.' 
                            : 'Process incoming purchases, update tracking details, and coordinate global delivery.'}
                    </p>
                </div>

                <button 
                    onClick={loadOrders}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#ffffff', border: '1px solid var(--seller-border, #e5e7eb)', color: 'var(--seller-text, #111827)', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                >
                    <RefreshCw size={15} />
                    <span>{rtl ? 'تحديث البيانات' : 'Refresh'}</span>
                </button>
            </div>

            {/* Metrics Ribbon */}
            <div className="seller-grid-cards" style={{ marginBottom: 28 }}>
                <div className="seller-stat-card">
                    <div className="seller-stat-info">
                        <h4>{rtl ? 'إجمالي الطلبات' : 'Total Orders'}</h4>
                        <p>{orders.length}</p>
                    </div>
                    <div className="seller-stat-icon" style={{ background: 'rgba(254,44,85,0.1)', color: '#FE2C55' }}>
                        <ShoppingBag size={22} />
                    </div>
                </div>

                <div className="seller-stat-card">
                    <div className="seller-stat-info">
                        <h4>{rtl ? 'طلبات قيد التجهيز والشحن' : 'Pending Fulfillment'}</h4>
                        <p style={{ color: '#d97706' }}>{pendingOrdersCount}</p>
                    </div>
                    <div className="seller-stat-icon" style={{ background: 'rgba(245,158,11,0.15)', color: '#d97706' }}>
                        <Clock size={22} />
                    </div>
                </div>

                <div className="seller-stat-card">
                    <div className="seller-stat-info">
                        <h4>{rtl ? 'إجمالي المبيعات' : 'Gross Volume'}</h4>
                        <p style={{ color: '#10b981' }}>${totalRevenue.toLocaleString()}</p>
                    </div>
                    <div className="seller-stat-icon" style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>
                        <DollarSign size={22} />
                    </div>
                </div>
            </div>

            {/* Search and Filter Tabs */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                    {['ALL', 'PENDING', 'SHIPPED', 'DELIVERED'].map((filterKey) => (
                        <button
                            key={filterKey}
                            onClick={() => setActiveFilter(filterKey)}
                            style={{
                                background: activeFilter === filterKey ? '#FE2C55' : 'var(--seller-card, #ffffff)',
                                color: activeFilter === filterKey ? '#fff' : 'var(--seller-text, #4b5563)',
                                border: activeFilter === filterKey ? '1px solid #FE2C55' : '1px solid var(--seller-border, #e5e7eb)',
                                padding: '8px 16px',
                                borderRadius: 6,
                                fontWeight: 600,
                                fontSize: 13,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {filterKey === 'ALL' && (rtl ? 'كافة الطلبات' : 'All Orders')}
                            {filterKey === 'PENDING' && (rtl ? 'قيد التجهيز' : 'Pending')}
                            {filterKey === 'SHIPPED' && (rtl ? 'تم الشحن' : 'Shipped')}
                            {filterKey === 'DELIVERED' && (rtl ? 'تم التوصيل' : 'Delivered')}
                        </button>
                    ))}
                </div>

                <div style={{ position: 'relative', width: 280 }}>
                    <input 
                        type="text"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        placeholder={rtl ? 'بحث برقم الطلب أو التتبع...' : 'Search order # or tracking...'}
                        style={{ width: '100%', padding: '10px 14px 10px 36px', background: 'var(--seller-input-bg, #ffffff)', border: '1px solid var(--seller-input-border, #d1d5db)', borderRadius: 8, color: 'var(--seller-text, #111827)', fontSize: 13, boxSizing: 'border-box' }}
                    />
                    <Search size={16} color="#64748b" style={{ position: 'absolute', left: 12, top: 12 }} />
                </div>
            </div>

            {/* Orders Table */}
            {loading ? (
                <div style={{ padding: '60px 0', textAlign: 'center' }}>
                    <Spinner />
                </div>
            ) : filteredOrders.length === 0 ? (
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, padding: '50px 20px', textAlign: 'center', color: 'var(--seller-muted, #64748b)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <ShoppingBag size={44} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <p style={{ margin: 0, fontSize: 14 }}>
                        {rtl ? 'لا توجد طلبات واردة في هذا القسم حالياً.' : 'No orders found matching this view.'}
                    </p>
                </div>
            ) : (
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: rtl ? 'right' : 'left', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#f8f9fa', borderBottom: '1px solid var(--seller-border, #e5e7eb)', color: 'var(--seller-muted, #64748b)' }}>
                                    <th style={{ padding: '14px 18px', fontWeight: 600 }}>{rtl ? 'رقم الطلب' : 'Order #'}</th>
                                    <th style={{ padding: '14px 18px', fontWeight: 600 }}>{rtl ? 'التاريخ' : 'Date'}</th>
                                    <th style={{ padding: '14px 18px', fontWeight: 600 }}>{rtl ? 'المنتجات' : 'Items'}</th>
                                    <th style={{ padding: '14px 18px', fontWeight: 600 }}>{rtl ? 'القيمة' : 'Total'}</th>
                                    <th style={{ padding: '14px 18px', fontWeight: 600 }}>{rtl ? 'الحالة' : 'Status'}</th>
                                    <th style={{ padding: '14px 18px', fontWeight: 600 }}>{rtl ? 'شركة الشحن' : 'Carrier'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders.map(order => (
                                    <tr key={order.id} style={{ borderBottom: '1px solid var(--seller-border, #e5e7eb)' }}>
                                        <td style={{ padding: '16px 18px', fontWeight: 700, color: 'var(--seller-text, #111827)' }}>
                                            {order.orderNumber || `ORD-${order.id}`}
                                        </td>
                                        <td style={{ padding: '16px 18px', color: 'var(--seller-muted, #64748b)' }}>
                                            {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'Recent'}
                                        </td>
                                        <td style={{ padding: '16px 18px', color: 'var(--seller-text, #374151)' }}>
                                            {order.itemCount || 1} {rtl ? 'قطع' : 'items'}
                                        </td>
                                        <td style={{ padding: '16px 18px', fontWeight: 700, color: '#FE2C55' }}>
                                            ${order.totalAmount}
                                        </td>
                                        <td style={{ padding: '16px 18px' }}>
                                            <span style={{ 
                                                display: 'inline-flex', 
                                                alignItems: 'center', 
                                                gap: 4, 
                                                padding: '4px 10px', 
                                                borderRadius: 20, 
                                                fontSize: 11, 
                                                fontWeight: 700,
                                                background: order.status === 'DELIVERED' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                                                color: order.status === 'DELIVERED' ? '#059669' : '#d97706'
                                            }}>
                                                {order.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px 18px', color: 'var(--seller-muted, #64748b)' }}>
                                            {order.carrierName ? (
                                                <span>{order.carrierName} ({order.trackingNumber || 'Tracking'})</span>
                                            ) : (
                                                <span style={{ fontStyle: 'italic', color: '#64748b' }}>LUXVONO Express</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
